import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import Chart from "chart.js/auto";

/** Max day difference (inclusive calendar range up to 31 days). */
export function isExportRangeValid(fromIso, toIso) {
  if (!fromIso || !toIso) return false;
  const a = new Date(`${fromIso}T12:00:00`).getTime();
  const b = new Date(`${toIso}T12:00:00`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  const span = Math.round(Math.abs(b - a) / 86400000);
  return span <= 30;
}

/** If range exceeds 31 days, shorten `to` to `from` + 30 days. */
export function clampHistRange(fromIso, toIso) {
  if (!fromIso || !toIso) return { from: fromIso, to: toIso };
  let a = fromIso;
  let b = toIso;
  if (a > b) {
    const t = a;
    a = b;
    b = t;
  }
  const span = Math.round(
    (new Date(`${b}T12:00:00`).getTime() - new Date(`${a}T12:00:00`).getTime()) / 86400000
  );
  if (span <= 30) return { from: a, to: b };
  const nd = new Date(`${a}T12:00:00`);
  nd.setDate(nd.getDate() + 30);
  return { from: a, to: nd.toISOString().slice(0, 10) };
}

function excelColumnLetters(indexZeroBased) {
  let dividend = indexZeroBased + 1;
  let name = "";
  while (dividend > 0) {
    const mod = (dividend - 1) % 26;
    name = String.fromCharCode(65 + mod) + name;
    dividend = Math.floor((dividend - mod) / 26);
  }
  return name;
}

function statusShort(st) {
  if (st === "Present") return "P";
  if (st === "Absent") return "A";
  if (st === "Late") return "L";
  return "";
}

/** Wait for Chart.js to finish drawing (v4 update() is async). */
async function afterChartDrawn() {
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
}

/** Decode PNG data URL to bytes (validates PNG magic). */
function pngToUint8Array(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  const idx = dataUrl.indexOf("base64,");
  if (idx === -1) return null;
  const b64 = dataUrl.slice(idx + 7).replace(/\s/g, "");
  try {
    const binary = atob(b64);
    const len = binary.length;
    const out = new Uint8Array(len);
    for (let i = 0; i < len; i++) out[i] = binary.charCodeAt(i);
    if (len < 8 || out[0] !== 0x89 || out[1] !== 0x50 || out[2] !== 0x4e || out[3] !== 0x47) return null;
    return out;
  } catch {
    return null;
  }
}

async function renderPieDataUrl(grandTotals) {
  const p = grandTotals.present || 0;
  const a = grandTotals.absent || 0;
  const l = grandTotals.late || 0;
  if (p + a + l === 0) return null;
  const canvas = document.createElement("canvas");
  canvas.width = 420;
  canvas.height = 280;
  const chart = new Chart(canvas.getContext("2d"), {
    type: "doughnut",
    data: {
      labels: ["Present", "Absent", "Late"],
      datasets: [
        {
          data: [p, a, l],
          backgroundColor: ["#15803d", "#b91c1c", "#4f46e5"],
          borderWidth: 0,
        },
      ],
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        title: { display: true, text: "Attendance mix (all records in range)" },
        legend: { position: "bottom" },
      },
      animation: false,
    },
  });
  await chart.update("none");
  await afterChartDrawn();
  const url = canvas.toDataURL("image/png");
  chart.destroy();
  return url;
}

async function renderLineDataUrl(dailyTotals) {
  if (!dailyTotals.length) return null;
  const canvas = document.createElement("canvas");
  canvas.width = 520;
  canvas.height = 300;
  const chart = new Chart(canvas.getContext("2d"), {
    type: "line",
    data: {
      labels: dailyTotals.map((d) => d.date),
      datasets: [
        {
          label: "Present",
          data: dailyTotals.map((d) => d.present),
          borderColor: "#15803d",
          tension: 0.2,
          fill: false,
        },
        {
          label: "Absent",
          data: dailyTotals.map((d) => d.absent),
          borderColor: "#b91c1c",
          tension: 0.2,
          fill: false,
        },
        {
          label: "Late",
          data: dailyTotals.map((d) => d.late),
          borderColor: "#4f46e5",
          tension: 0.2,
          fill: false,
        },
      ],
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        title: { display: true, text: "Daily attendance counts" },
        legend: { position: "bottom" },
      },
      scales: {
        x: { ticks: { maxRotation: 45, minRotation: 45 } },
      },
      animation: false,
    },
  });
  await chart.update("none");
  await afterChartDrawn();
  const url = canvas.toDataURL("image/png");
  chart.destroy();
  return url;
}

export async function fetchExportPayload(apiBase, headers, { batch_id, from, to, admission_id }) {
  const qs = new URLSearchParams({
    batch_id: String(batch_id),
    from,
    to,
  });
  if (admission_id) qs.set("admission_id", String(admission_id));
  const base = String(apiBase || "").replace(/\/+$/, "");
  let res;
  try {
    res = await fetch(`${base}/admission/student-attendance/history/export-data?${qs}`, {
      headers,
    });
  } catch (e) {
    const msg =
      e && typeof e.message === "string" && e.message.includes("Failed to fetch")
        ? "Cannot reach API. Check REACT_APP_LINK / network, or run the backend and CORS settings."
        : e?.message || "Network error";
    throw new Error(msg);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.isError) {
    throw new Error(data.message || "Failed to load export data");
  }
  return data;
}

async function buildChartImages(payload) {
  const [pie, line] = await Promise.all([
    renderPieDataUrl(payload.grandTotals),
    renderLineDataUrl(payload.dailyTotals || []),
  ]);
  return { pie, line };
}

export async function exportAttendanceExcel(payload) {
  const { pie, line } = await buildChartImages(payload);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Attendance", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  const { dates, students, batchLabel, from, to, studentFilter } = payload;
  const filterLine = studentFilter
    ? `Batch: ${batchLabel} | ${from} → ${to} | Student: ${studentFilter.name}`
    : `Batch: ${batchLabel} | ${from} → ${to} | All students`;

  const colCount = 1 + dates.length + 3;
  const endCol = excelColumnLetters(colCount - 1);
  ws.mergeCells(`A1:${endCol}1`);
  ws.getCell("A1").value = "Attendance Report";
  ws.getCell("A1").font = { size: 16, bold: true };
  ws.mergeCells(`A2:${endCol}2`);
  ws.getCell("A2").value = filterLine;
  ws.getCell("A2").font = { size: 11 };

  const headerRow = 4;
  ws.getCell(headerRow, 1).value = "Student";
  ws.getCell(headerRow, 1).font = { bold: true };
  dates.forEach((d, i) => {
    ws.getCell(headerRow, 2 + i).value = d;
    ws.getCell(headerRow, 2 + i).font = { bold: true, size: 9 };
  });
  const t0 = 2 + dates.length;
  ws.getCell(headerRow, t0).value = "P";
  ws.getCell(headerRow, t0 + 1).value = "A";
  ws.getCell(headerRow, t0 + 2).value = "L";
  [t0, t0 + 1, t0 + 2].forEach((c) => {
    ws.getCell(headerRow, c).font = { bold: true };
  });

  students.forEach((stu, ri) => {
    const r = headerRow + 1 + ri;
    ws.getCell(r, 1).value = stu.name;
    dates.forEach((d, ci) => {
      ws.getCell(r, 2 + ci).value = statusShort(stu.cells[d] || "");
    });
    ws.getCell(r, t0).value = stu.totals.present;
    ws.getCell(r, t0 + 1).value = stu.totals.absent;
    ws.getCell(r, t0 + 2).value = stu.totals.late;
  });

  ws.columns.forEach((col, idx) => {
    col.width = idx === 0 ? 28 : idx <= dates.length ? 11 : 8;
  });

  /* 0-based row anchor; leave one blank row after the table so charts don't overlap the last student row */
  const chartRow0 = headerRow + students.length + 1;
  const pieBuf = pngToUint8Array(pie);
  if (pieBuf) {
    const img1 = wb.addImage({ buffer: pieBuf, extension: "png" });
    ws.addImage(img1, {
      tl: { col: 0, row: chartRow0 },
      ext: { width: 400, height: 260 },
    });
  }
  const lineBuf = pngToUint8Array(line);
  if (lineBuf) {
    const img2 = wb.addImage({ buffer: lineBuf, extension: "png" });
    ws.addImage(img2, {
      tl: { col: 6, row: chartRow0 },
      ext: { width: 480, height: 280 },
    });
  }

  const buf = await wb.xlsx.writeBuffer();
  const name = `attendance-${from}-to-${to}.xlsx`.replace(/:/g, "");
  saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), name);
}

export async function exportAttendancePdf(payload) {
  const { pie, line } = await buildChartImages(payload);
  const { dates, students, batchLabel, from, to, studentFilter } = payload;
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  let y = 36;

  doc.setFontSize(16);
  doc.text("Attendance Report", 40, y);
  y += 22;
  doc.setFontSize(10);
  const sub = studentFilter
    ? `Batch: ${batchLabel}  |  Range: ${from} to ${to}  |  Student: ${studentFilter.name} (${studentFilter.admission_id || ""})`
    : `Batch: ${batchLabel}  |  Range: ${from} to ${to}  |  All students`;
  const splitSub = doc.splitTextToSize(sub, pageW - 80);
  doc.text(splitSub, 40, y);
  y += 14 * splitSub.length + 10;

  const addPdfPng = (dataUrl, x, w, h) => {
    const u8 = pngToUint8Array(dataUrl);
    if (!u8) return;
    try {
      doc.addImage(u8, "PNG", x, y, w, h);
    } catch (err) {
      try {
        doc.addImage(dataUrl, "PNG", x, y, w, h);
      } catch (err2) {
        console.warn("PDF chart image skipped:", err2?.message || err2);
      }
    }
  };
  if (pie) addPdfPng(pie, 40, 260, 173);
  if (line) addPdfPng(line, 320, 300, 173);
  y += pie || line ? 190 : 10;

  /* Fixed column grid: headers and rows must use identical left edges and widths */
  const margin = 40;
  const usableW = pageW - 2 * margin;
  const nDates = dates.length;
  const sumColW = 20;
  const nameColW = Math.min(118, Math.max(72, usableW * 0.18));
  const dateColW =
    nDates > 0
      ? Math.max(16, (usableW - nameColW - 3 * sumColW) / nDates)
      : 0;

  const colLeft = [];
  let cur = margin;
  colLeft.push(cur);
  cur += nameColW;
  for (let i = 0; i < nDates; i++) {
    colLeft.push(cur);
    cur += dateColW;
  }
  colLeft.push(cur);
  cur += sumColW;
  colLeft.push(cur);
  cur += sumColW;
  colLeft.push(cur);

  const headerLabels = ["Student", ...dates.map((d) => d.slice(5)), "P", "A", "L"];
  const colWidths = [
    nameColW,
    ...Array(nDates).fill(dateColW),
    sumColW,
    sumColW,
    sumColW,
  ];

  const drawHeader = () => {
    doc.setFontSize(6);
    doc.setFont("helvetica", "bold");
    headerLabels.forEach((h, i) => {
      doc.text(String(h), colLeft[i], y, { maxWidth: colWidths[i] });
    });
    doc.setFont("helvetica", "normal");
    y += 12;
  };

  drawHeader();

  doc.setFontSize(5.5);
  for (const stu of students) {
    if (y > pageH - 36) {
      doc.addPage();
      y = 36;
      drawHeader();
    }
    const rowY = y;
    let nameLines = doc.splitTextToSize(String(stu.name || "—"), nameColW - 2);
    if (nameLines.length > 3) nameLines = nameLines.slice(0, 3);
    doc.text(nameLines.join("\n"), colLeft[0], rowY, {
      maxWidth: nameColW - 2,
      lineHeightFactor: 1.12,
    });
    dates.forEach((d, i) => {
      doc.text(statusShort(stu.cells[d] || ""), colLeft[1 + i], rowY, {
        maxWidth: dateColW,
      });
    });
    const pIdx = 1 + nDates;
    doc.text(String(stu.totals.present), colLeft[pIdx], rowY, { maxWidth: sumColW });
    doc.text(String(stu.totals.absent), colLeft[pIdx + 1], rowY, { maxWidth: sumColW });
    doc.text(String(stu.totals.late), colLeft[pIdx + 2], rowY, { maxWidth: sumColW });
    const lineCount = nameLines.length;
    const nameH = Math.max(10, lineCount * 6 + 4);
    y = rowY + nameH;
  }

  const fname = `attendance-${from}-to-${to}.pdf`.replace(/:/g, "");
  doc.save(fname);
}
